// Beat 7 visual: a fixed full-bleed wheel that sits low on the screen.
//
//   1. Entrance: the arc LINE draws in from the right edge, across the
//      screen, connecting to the left edge (a stroke reveal — the wheel
//      itself never moves position).
//   2. Once connected it continues to rotate smoothly, in place, forever
//      while the beat is on screen. The rim gradient turns with the ring.
//   3. Icons ride the ring: as one exits left, the next enters from the
//      right, one at a time. Each pauses-by-proximity at the top focus
//      slot, where its (small) caption cross-fades in below the arc.
//
// The wheel's screen position is constant throughout — only the stroke
// reveal (entrance) and opacity (entrance/exit) change. No translation, no
// shoot/lift. Choreography is locked to the Beat 7 hero text segment.
// prefers-reduced-motion: a static stacked list instead.
//
// Geometry is computed in live viewport px (viewBox = w x h, 1:1). Knobs
// are under "VISUAL TUNABLES".

import React from 'react';
import { motion, useTransform, type MotionValue } from 'framer-motion';
import { HandHeart, Lightbulb, Megaphone, Users } from 'lucide-react';

import { getColorStop, type ColorStop } from '../../../styles/playerColors';
import {
  easeOutCubic,
  lerp,
  mapProgress,
} from '../../../utilities/scrollTimelineMath';

type BeatSevenSupportArcProps = {
  scrollVh: MotionValue<number>;
  segmentStartVh: number;
  segmentDurationVh: number;
};

// ───────────────────────── VISUAL TUNABLES ─────────────────────────
/** Rim radius as a multiple of viewport width (bigger = flatter arc). */
const RADIUS_W_FACTOR = 1.2;
/** Rim apex (top of the arc) as a fraction of viewport height. Large =
 *  the whole composition sits low on the screen. */
const APEX_Y_FRAC = 0.66;
/** Icons ride this far outside (above) the rim, as a multiple of the icon
 *  size — so the gap scales with the icon (bigger on phones). */
const GAP_OF_ICON = 0.85;
/** Icon box edge px: clamp(min, ICON_W_FRAC * w, max). The floor is high
 *  so icons/text stay legible on phones (where ICON_W_FRAC * w is tiny). */
const ICON_W_FRAC = 0.08;
const ICON_MIN_PX = 62;
const ICON_MAX_PX = 150;
/** Rim stroke width px: clamp(min, STROKE_W_FRAC * w, max). */
const STROKE_W_FRAC = 0.0055;
const STROKE_MIN_PX = 3;
const STROKE_MAX_PX = 11;
/** Caption: smaller than the icon; panel sits well below the apex. */
const CAPTION_FONT_OF_ICON = 0.3;
const CAPTION_MIN_FONT_PX = 14;
const CAPTION_W_FRAC = 0.4;
const CAPTION_TOP_GAP_FRAC = 0.11;
/** Box height: CAPTION_H_FRAC * h, but with a px floor. The font size has a
 *  floor (via iconBase) so on short screens the viewport-fraction box can't
 *  hold the longest caption wrapped at the floor font size — the last line's
 *  descenders clip against the foreignObject edge. The floor only engages on
 *  short screens; taller phones already exceed it. Captions are top-anchored,
 *  so the extra height is empty space below — no layout shift. */
const CAPTION_H_FRAC = 0.16;
const CAPTION_MIN_H_PX = 132;
/** Half-angle (deg) past which an icon has faded out of view. */
const WINDOW_DEG = 26;
const WINDOW_FADE_DEG = 7;
/** Angle (deg) over which scale/caption fall from focus -> minimum. */
const FALLOFF_DEG = 22;
const FOCUS_SCALE = 1;
const MIN_SCALE = 0.5;
/** Off-right margin (deg) so an icon enters from the edge, not mid-screen. */
const EDGE_MARGIN_DEG = 12;

const SLOT_DEG = 90;
const NUM_PILLARS = 4;
/** First icon starts just off the right; slots step left by SLOT_DEG. */
const SLOT_OFFSET_DEG = WINDOW_DEG + EDGE_MARGIN_DEG;
/** Rotation (negative = turning so icons travel right -> left) that brings
 *  the last icon fully past the left edge. */
const SWEEP_END_ROTATION_DEG = -(
  SLOT_OFFSET_DEG +
  (NUM_PILLARS - 1) * SLOT_DEG +
  WINDOW_DEG +
  EDGE_MARGIN_DEG
);
/** Extra rotation during the opacity exit. Keep at 0: negative values spin the
 *  wheel back far enough that pillar 0 re-enters WINDOW_DEG before fade-out
 *  finishes (~34° is enough to peek; -60° puts it near center again). */
const EXIT_EXTRA_ROTATION_DEG = 0;
/** Rotation at which each icon i is exactly at the focus slot (center). */
const STATION_ROTATIONS = Array.from(
  { length: NUM_PILLARS },
  (_, k) => -(SLOT_OFFSET_DEG + k * SLOT_DEG)
);
const easeIn = (t: number): number => t * t * t;
const easeOut = (t: number): number => 1 - Math.pow(1 - t, 3);
const easeInOut = (t: number): number => 0.5 - 0.5 * Math.cos(Math.PI * t);

// Each icon's visible life is: a slow eased APPROACH (it scales/fades in as
// it nears center), a HOLD dead-center (the catch), a slow eased DEPART (it
// scales/fades out), then a quick off-screen FAST spin to the next icon.
// Slowing only the APPROACH/DEPART (the part where the icon crosses the
// visible window) makes the appear/disappear smooth without lengthening the
// whole beat. Weights = share of the sweep each phase gets; raise
// APPROACH/DEPART for a slower, smoother enter/exit.
const LEAD_WEIGHT = 0.8; // off-right → icon 0 about to enter view
const APPROACH_WEIGHT = 1.6; // visible: icon scales/fades into center
const DWELL_WEIGHT = 1.4; // the pause, pinned dead-center
const DEPART_WEIGHT = 1.6; // visible: icon scales/fades back out
const FAST_WEIGHT = 0.9; // off-screen spin between icons
const TAIL_WEIGHT = 0.9; // last icon out → fully off-left
/** Rotation (deg) an icon travels while crossing the visible window on each
 *  side of center. Matches the icon's fade/scale window. */
const RAMP_DEG = WINDOW_DEG;

type EaseKind = 'in' | 'out' | 'inout' | 'hold';
type SweepSegment = {
  p0: number;
  p1: number;
  r0: number;
  r1: number;
  ease: EaseKind;
};

/** Ordered phases built once from the weights above:
 *  LEAD, then per station [APPROACH, DWELL, DEPART] with a FAST spin
 *  between stations, then TAIL. */
const SWEEP_SEGMENTS: SweepSegment[] = (() => {
  const phases: Array<{
    weight: number;
    r0: number;
    r1: number;
    ease: EaseKind;
  }> = [];
  const c0 = STATION_ROTATIONS[0]!;
  phases.push({ weight: LEAD_WEIGHT, r0: 0, r1: c0 + RAMP_DEG, ease: 'in' });
  for (let k = 0; k < NUM_PILLARS; k++) {
    const c = STATION_ROTATIONS[k]!;
    phases.push({
      weight: APPROACH_WEIGHT,
      r0: c + RAMP_DEG,
      r1: c,
      ease: 'out',
    });
    phases.push({ weight: DWELL_WEIGHT, r0: c, r1: c, ease: 'hold' });
    phases.push({ weight: DEPART_WEIGHT, r0: c, r1: c - RAMP_DEG, ease: 'in' });
    if (k < NUM_PILLARS - 1) {
      phases.push({
        weight: FAST_WEIGHT,
        r0: c - RAMP_DEG,
        r1: STATION_ROTATIONS[k + 1]! + RAMP_DEG,
        ease: 'inout',
      });
    }
  }
  phases.push({
    weight: TAIL_WEIGHT,
    r0: STATION_ROTATIONS[NUM_PILLARS - 1]! - RAMP_DEG,
    r1: SWEEP_END_ROTATION_DEG,
    ease: 'inout',
  });
  const total = phases.reduce((sum, ph) => sum + ph.weight, 0);
  let acc = 0;
  return phases.map((ph) => {
    const p0 = acc / total;
    acc += ph.weight;
    return { p0, p1: acc / total, r0: ph.r0, r1: ph.r1, ease: ph.ease };
  });
})();

const applyEase = (t: number, kind: EaseKind): number => {
  if (kind === 'in') return easeIn(t);
  if (kind === 'out') return easeOut(t);
  return easeInOut(t);
};

// Choreography (%, of the host segment — locked to the hero text segment).
const ENTER_START_PCT = 8;
const ENTER_END_PCT = 24;
const SWEEP_END_PCT = 80;
const EXIT_END_PCT = 100;
/** Px the composition lifts on exit — 0: position never changes. */
// (intentionally no lift; the wheel must hold its position)
// ────────────────────────────────────────────────────────────────────

const CAPTION_COLOR = getColorStop('blue', '900');
const ICON_COLOR = getColorStop('blue', '800');
/** Rim stroke: light → dark across the screen (left → right), one stop per step. */
const RIM_GRAD_STOPS: ColorStop[] = [
  '300',
  '400',
  '500',
  '600',
  '700',
  '800',
  '900',
];

type Pillar = { id: string; caption: string; icon: React.ReactNode };

const ICON_CLASS = 'h-full w-full';

// Order = the order pillars reach the focus slot as the wheel turns.
const PILLARS: Pillar[] = [
  {
    id: 'premium-support',
    caption: 'Premium support with fast answers from a real person.',
    icon: <HandHeart className={ICON_CLASS} strokeWidth={1.75} />,
  },
  {
    id: 'community',
    caption: 'Discord with developer access and streamers worth knowing.',
    icon: <Users className={ICON_CLASS} strokeWidth={1.75} />,
  },
  {
    id: 'feedback',
    caption: 'Your ideas implemented quickly to shape our future.',
    icon: <Lightbulb className={ICON_CLASS} strokeWidth={1.75} />,
  },
  {
    id: 'amplify',
    caption: 'Your channel amplified on our website and socials.',
    icon: <Megaphone className={ICON_CLASS} strokeWidth={1.75} />,
  },
];

const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mql.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return reduced;
};

const useViewportSize = (): { w: number; h: number } => {
  const [size, setSize] = React.useState(() =>
    typeof window === 'undefined'
      ? { w: 0, h: 0 }
      : { w: window.innerWidth, h: window.innerHeight }
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const update = () =>
      setSize({ w: window.innerWidth, h: window.innerHeight });
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return size;
};

const normalizeAngle = (deg: number): number => {
  let a = deg % 360;
  if (a > 180) a -= 360;
  if (a <= -180) a += 360;
  return a;
};

/** Wheel rotation (deg). 0 while the line draws in (the wheel does not move
 *  during entrance), then the keyframed sweep (travel → real pause at each
 *  centered icon → travel), continuing a touch further while it fades on
 *  exit. */
const wheelRotationDeg = (pct: number): number => {
  if (pct <= ENTER_END_PCT) return 0;
  if (pct < SWEEP_END_PCT) {
    const sp = (pct - ENTER_END_PCT) / (SWEEP_END_PCT - ENTER_END_PCT);
    const seg =
      SWEEP_SEGMENTS.find((s) => sp <= s.p1) ??
      SWEEP_SEGMENTS[SWEEP_SEGMENTS.length - 1]!;
    if (seg.ease === 'hold') return seg.r0; // the catch: icon pinned center
    const t = (sp - seg.p0) / Math.max(seg.p1 - seg.p0, 1e-6);
    return lerp(seg.r0, seg.r1, applyEase(t, seg.ease));
  }
  const pe = mapProgress(pct, SWEEP_END_PCT, EXIT_END_PCT);
  return SWEEP_END_ROTATION_DEG + pe * EXIT_EXTRA_ROTATION_DEG;
};

const focusDelta = (slotDeg: number, rotation: number): number =>
  normalizeAngle(slotDeg + rotation);

const focusProximity = (deltaDeg: number): number =>
  easeOutCubic(1 - Math.min(Math.abs(deltaDeg) / FALLOFF_DEG, 1));

const windowVisibility = (deltaDeg: number): number => {
  const a = Math.abs(deltaDeg);
  if (a >= WINDOW_DEG) return 0;
  if (a <= WINDOW_DEG - WINDOW_FADE_DEG) return 1;
  return (WINDOW_DEG - a) / WINDOW_FADE_DEG;
};

const clampPx = (v: number, min: number, max: number): number =>
  Math.min(Math.max(v, min), max);

const BeatSevenSupportArc: React.FC<BeatSevenSupportArcProps> = ({
  scrollVh,
  segmentStartVh,
  segmentDurationVh,
}) => {
  const reducedMotion = usePrefersReducedMotion();
  const { w, h } = useViewportSize();

  const toVh = (pct: number) =>
    segmentStartVh + (pct / 100) * segmentDurationVh;
  const enterStartVh = toVh(ENTER_START_PCT);
  const enterEndVh = toVh(ENTER_END_PCT);
  const exitStartVh = toVh(SWEEP_END_PCT);
  const exitEndVh = toVh(EXIT_END_PCT);

  // Opacity is the only thing that changes besides rotation/reveal — the
  // wheel's position is constant. 0 before entrance and after exit.
  const rootOpacity = useTransform(scrollVh, (v) => {
    const inN = easeOutCubic(mapProgress(v, enterStartVh, enterEndVh));
    const outN = easeOutCubic(mapProgress(v, exitStartVh, exitEndVh));
    return inN * (1 - outN);
  });

  // Stroke reveal: 0 -> 1 across entrance (draws right -> left), then full.
  const reveal = useTransform(scrollVh, (v) =>
    easeOutCubic(mapProgress(v, enterStartVh, enterEndVh))
  );

  const rotationDeg = useTransform(scrollVh, (v) => {
    if (reducedMotion) return 0;
    const pct = ((v - segmentStartVh) / segmentDurationVh) * 100;
    return wheelRotationDeg(pct);
  });

  if (reducedMotion) {
    return (
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 flex items-end justify-center px-8 pb-16"
        style={{ opacity: rootOpacity }}
      >
        <ul className="flex w-full max-w-xl flex-col gap-4 sm:gap-5">
          {PILLARS.map((pillar) => (
            <li
              key={pillar.id}
              className="flex items-center gap-3 sm:gap-4"
              style={{ color: ICON_COLOR }}
            >
              <span className="h-8 w-8 shrink-0 sm:h-10 sm:w-10">
                {pillar.icon}
              </span>
              <span
                className="font-semibold text-sm sm:text-base"
                style={{ color: CAPTION_COLOR }}
              >
                {pillar.caption}
              </span>
            </li>
          ))}
        </ul>
      </motion.div>
    );
  }

  if (w === 0 || h === 0) return null;

  const centerX = w / 2;
  const radius = RADIUS_W_FACTOR * w;
  const apexY = APEX_Y_FRAC * h;
  const centerY = apexY + radius; // far below the viewport
  const iconBase = clampPx(ICON_W_FRAC * w, ICON_MIN_PX, ICON_MAX_PX);
  // Icons ride just OUTSIDE the rim (above the apex on a ∩ arc); the gap
  // scales with the icon so the separation looks right at every size.
  const ringIconRadius = radius + GAP_OF_ICON * iconBase;
  const strokeWidth = clampPx(STROKE_W_FRAC * w, STROKE_MIN_PX, STROKE_MAX_PX);
  const fontPx = Math.max(
    Math.round(iconBase * CAPTION_FONT_OF_ICON),
    CAPTION_MIN_FONT_PX
  );
  const captionW = CAPTION_W_FRAC * w;
  const captionH = Math.max(CAPTION_H_FRAC * h, CAPTION_MIN_H_PX);

  // Ring split in local (pre-rotation, apex-up) coords: the visible top arc
  // (drawn during entrance, right -> left) and the off-screen remainder
  // (always present so it rotates into view as the wheel turns).
  const halfChord = w / 2; // screen edge x-distance from center
  const yEdge = -Math.sqrt(
    Math.max(radius * radius - halfChord * halfChord, 1)
  );
  const pR = `${halfChord.toFixed(2)} ${yEdge.toFixed(2)}`; // right edge
  const pL = `${(-halfChord).toFixed(2)} ${yEdge.toFixed(2)}`; // left edge
  // seg1: right edge -> over apex -> left edge (minor arc, sweep 0).
  const topArcPath = `M ${pR} A ${radius} ${radius} 0 0 0 ${pL}`;
  // seg2: left edge -> around the bottom -> right edge (major arc, sweep 0).
  const restArcPath = `M ${pL} A ${radius} ${radius} 0 1 0 ${pR}`;

  return (
    <motion.div
      // Decorative — the hero panel carries this beat's meaning.
      aria-hidden="true"
      className="absolute inset-0 overflow-hidden"
      style={{ opacity: rootOpacity }}
    >
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className="absolute inset-0 h-full w-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient
            id="beat7-rim"
            gradientUnits="userSpaceOnUse"
            x1={-radius * 2}
            y1={0}
            x2={radius * 2}
            y2={0}
          >
            {RIM_GRAD_STOPS.map((stop, i) => (
              <stop
                key={stop}
                offset={`${(i / (RIM_GRAD_STOPS.length - 1)) * 100}%`}
                stopColor={getColorStop('blue', stop)}
              />
            ))}
          </linearGradient>
        </defs>

        <g transform={`translate(${centerX} ${centerY})`}>
          <motion.g style={{ rotate: rotationDeg }}>
            {/* Off-screen remainder — always drawn so it rotates into view. */}
            <path
              d={restArcPath}
              fill="none"
              stroke="url(#beat7-rim)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
            {/* Visible top arc — reveals right -> left during entrance. */}
            <motion.path
              d={topArcPath}
              fill="none"
              stroke="url(#beat7-rim)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              style={{ pathLength: reveal }}
            />
            {PILLARS.map((pillar, index) => (
              <WheelIcon
                key={pillar.id}
                pillar={pillar}
                slotDeg={SLOT_OFFSET_DEG + index * SLOT_DEG}
                iconRadius={ringIconRadius}
                iconBase={iconBase}
                rotationDeg={rotationDeg}
              />
            ))}
          </motion.g>
        </g>

        {/* Captions: static (no rotation), small, below the apex. */}
        <foreignObject
          x={centerX - captionW / 2}
          y={apexY + CAPTION_TOP_GAP_FRAC * h}
          width={captionW}
          height={captionH}
        >
          <div className="relative h-full w-full">
            {PILLARS.map((pillar, index) => (
              <WheelCaption
                key={pillar.id}
                pillar={pillar}
                slotDeg={SLOT_OFFSET_DEG + index * SLOT_DEG}
                fontPx={fontPx}
                rotationDeg={rotationDeg}
              />
            ))}
          </div>
        </foreignObject>
      </svg>
    </motion.div>
  );
};

type WheelIconProps = {
  pillar: Pillar;
  slotDeg: number;
  iconRadius: number;
  iconBase: number;
  rotationDeg: MotionValue<number>;
};

const WheelIcon: React.FC<WheelIconProps> = ({
  pillar,
  slotDeg,
  iconRadius,
  iconBase,
  rotationDeg,
}) => {
  const counterRotate = useTransform(rotationDeg, (r) => -(r + slotDeg));
  const scale = useTransform(rotationDeg, (r) => {
    const p = focusProximity(focusDelta(slotDeg, r));
    return MIN_SCALE + (FOCUS_SCALE - MIN_SCALE) * p;
  });
  const opacity = useTransform(rotationDeg, (r) =>
    windowVisibility(focusDelta(slotDeg, r))
  );

  return (
    <g transform={`rotate(${slotDeg}) translate(0 ${-iconRadius})`}>
      <motion.g style={{ rotate: counterRotate, scale, opacity }}>
        <foreignObject
          x={-iconBase / 2}
          y={-iconBase / 2}
          width={iconBase}
          height={iconBase}
        >
          <div className="h-full w-full" style={{ color: ICON_COLOR }}>
            {pillar.icon}
          </div>
        </foreignObject>
      </motion.g>
    </g>
  );
};

type WheelCaptionProps = {
  pillar: Pillar;
  slotDeg: number;
  fontPx: number;
  rotationDeg: MotionValue<number>;
};

const WheelCaption: React.FC<WheelCaptionProps> = ({
  pillar,
  slotDeg,
  fontPx,
  rotationDeg,
}) => {
  const opacity = useTransform(rotationDeg, (r) =>
    focusProximity(focusDelta(slotDeg, r))
  );
  return (
    <motion.p
      className="absolute inset-0 flex items-start justify-center text-center font-semibold leading-tight"
      style={{
        opacity,
        color: CAPTION_COLOR,
        fontSize: `${fontPx}px`,
        lineHeight: 1.15,
      }}
    >
      {pillar.caption}
    </motion.p>
  );
};

export default BeatSevenSupportArc;
